import { useState } from "react";
import { ALL_KINDS, PROFILES, PROFILE_TITLES, WINE_PROFILE, isKnownLoss, kindProfile, shrinkageReport, stockLines } from "@lagerlog/core";
import type { ShrinkageRow, StockLine } from "@lagerlog/core";
import { Containers, containerWords, drinkProfile, euro, inUnits, otherContainers } from "../catalog.ts";
import type { Catalog } from "../catalog.ts";
import { useCatalog, useDrinks, useLedger, useStore } from "../hooks.ts";
import type { State } from "@lagerlog/core";
import { download, euroCell, toCsv } from "../download.ts";
import { Lines, Parts } from "./Stock.tsx";
import { fmt, fmtDiff } from "./Book.tsx";
import { SubNav } from "./SubNav.tsx";
import { useWide } from "../wide.ts";

const day = (at: number) => new Date(at).toLocaleDateString("sv-SE");
const endOf = (d: string) => new Date(`${d}T23:59:59.999`).getTime();
const startOf = (d: string) => new Date(`${d}T00:00:00`).getTime();
const de = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString("de-DE");

/**
 * Reports (P1-8): stock and value at a date (R7) and shrinkage in a period (R4). The device holds
 * only the days after its snapshot; the stock at a date and longer periods come from the server.
 */
export function Reports() {
  const catalog = useCatalog();
  const ledger = useLedger();
  if (!catalog || !ledger) return null;
  return (
    <div className="reports">
      <SubNav current="auswertung" />
      <div className="split wide-even">
        <div className="pane">
          <AtDate catalog={catalog} />
        </div>
        <div className="pane">
          <Shrinkage catalog={catalog} cutoff={ledger.cutoff} />
        </div>
      </div>
      <ByCategory catalog={catalog} />
    </div>
  );
}

function AtDate({ catalog }: { catalog: Catalog }) {
  const { store } = useStore();
  const drinks = useDrinks();
  // The end of last month: what the tax adviser usually asks for.
  const [date, setDate] = useState(() => day(new Date(new Date().getFullYear(), new Date().getMonth(), 0).getTime()));
  const [lines, setLines] = useState<StockLine[] | null>(null);
  const [shownDate, setShownDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lagers = store.config?.lagers ?? [];

  async function load() {
    setBusy(true);
    setError(null);
    try {
      const res = await store.request<{ lines: StockLine[] }>(`/api/stock?at=${endOf(date)}&lager=${lagers.join(",")}`, {}, 60_000);
      setLines(res.lines);
      setShownDate(date);
    } catch {
      setError("Der Bestand zum Stichtag kommt vom Server: Bitte mit Netz noch einmal versuchen.");
    } finally {
      setBusy(false);
    }
  }

  const rows = lines ? group(lines) : [];
  const total = rows.reduce((s, r) => s + r.value, 0);
  const name = (id: string) => catalog.articles.get(id)?.name ?? id;

  // Staff and viewers see quantities only (F20).
  const prices = store.prices;
  function csv() {
    const wine = drinks ? "Getränk" : "Wein";
    // Kegs and cans among the bottles: the quantity with its container in a column of its own.
    const mixed = otherContainers(rows.map((r) => catalog.articles.get(r.article)));
    const head = ["Lager", wine, ...(mixed ? ["Menge", "Gebinde"] : ["Flaschen"])];
    const out: unknown[][] = [prices ? [...head, "Wert (€)", "ohne Preis"] : head];
    for (const r of rows) {
      const a = catalog.articles.get(r.article);
      const qty = [catalog.lagers.get(r.lager)?.name ?? r.lager, name(r.article), inUnits(a, r.qty), ...(mixed ? [a ? containerWords(a).many : ""] : [])];
      out.push(prices ? [...qty, euroCell(r.value), r.unpriced ? "ja" : ""] : qty);
    }
    download(`Bestand zum ${de(shownDate)}.csv`, toCsv(out), "text/csv;charset=utf-8");
  }

  return (
    <section className="card">
      <h2>Bestand zum Stichtag</h2>
      <p className="muted">{prices ? "Bestand und Wert zum Einkaufspreis" : "Bestand"} am Ende des Tages, etwa für die Steuerberatung. Spätere Stornos zählen zurück: Eine stornierte Buchung war nie gebucht.</p>
      <div className="row dates">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="at-date" />
        <button className="primary" disabled={busy || !date} onClick={load} data-testid="at-load">
          {busy ? "Rechnet …" : "Anzeigen"}
        </button>
      </div>
      {error && <p className="error-box">{error}</p>}
      {lines && (
        <>
          <p className="row between">
            <strong>
              {de(shownDate)}: {rows.reduce((c, r) => c.add(catalog.articles.get(r.article), r.qty), new Containers()).text()}
            </strong>
            {prices && <strong>{euro(total)}</strong>}
          </p>
          <ul className="list">
            {rows.map((r) => (
              <li key={`${r.lager}|${r.article}`} className="row between">
                <span>
                  {name(r.article)}
                  <br />
                  <span className="muted">
                    {catalog.lagers.get(r.lager)?.name ?? r.lager}
                    {prices && r.unpriced ? " · teils ohne Preis" : ""}
                  </span>
                </span>
                <span className="side">
                  <Parts a={catalog.articles.get(r.article)} qty={r.qty} />
                  {prices && <span className="muted">{euro(r.value)}</span>}
                </span>
              </li>
            ))}
          </ul>
          <button onClick={csv}>Als Excel-Tabelle (CSV)</button>
        </>
      )}
    </section>
  );
}

function group(lines: StockLine[]) {
  const map = new Map<string, { lager: string; article: string; qty: number; value: number; unpriced: boolean }>();
  for (const l of lines) {
    const key = `${l.lager}|${l.article}`;
    const r = map.get(key) ?? { lager: l.lager, article: l.article, qty: 0, value: 0, unpriced: false };
    r.qty += l.qty;
    r.value += l.value;
    r.unpriced ||= l.unpriced;
    map.set(key, r);
  }
  return [...map.values()].filter((r) => r.qty !== 0).sort((a, b) => a.lager.localeCompare(b.lager) || b.value - a.value);
}

function Shrinkage({ catalog, cutoff }: { catalog: Catalog; cutoff: number | null }) {
  const { store } = useStore();
  const drinks = useDrinks();
  const now = new Date();
  const [from, setFrom] = useState(day(new Date(now.getFullYear(), now.getMonth(), 1).getTime()));
  const [to, setTo] = useState(day(now.getTime()));
  const [rows, setRows] = useState<ShrinkageRow[] | null>(null);
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lagers = store.config?.lagers ?? [];

  async function load() {
    setBusy(true);
    setError(null);
    try {
      const a = startOf(from);
      const b = endOf(to);
      // What the device holds it computes itself, also without network.
      if (cutoff !== null && a >= cutoff && store.replica) {
        setRows(shrinkageReport(store.replica.state(), store.replica.journal(), a, b, new Set(lagers), isKnownLoss));
        setSource("auf diesem Gerät gerechnet");
      } else {
        const res = await store.request<{ rows: ShrinkageRow[] }>(`/api/shrinkage?from=${a}&to=${b}&lager=${lagers.join(",")}`, {}, 60_000);
        setRows(res.rows);
        setSource("vom Server, über das ganze Journal");
      }
    } catch {
      setError("Für diesen Zeitraum braucht es den Server: Bitte mit Netz noch einmal versuchen.");
    } finally {
      setBusy(false);
    }
  }

  const name = (id: string) => catalog.articles.get(id)?.name ?? id;
  const sum = (f: (r: ShrinkageRow) => number) => (rows ?? []).reduce((s, r) => s + f(r), 0);
  /** Bottles of a quantity of the row's wine; sums add bottles, not ml, and kegs apart from bottles. */
  const fl = (r: ShrinkageRow, qty: number) => inUnits(catalog.articles.get(r.article), qty);
  const total = (f: (r: ShrinkageRow) => number, sign = false) => (rows ?? []).reduce((c, r) => c.add(catalog.articles.get(r.article), f(r)), new Containers()).parts(sign);

  const prices = store.prices;
  function csv() {
    // Kegs and cans among the bottles: a column says the container, the headings no longer „Fl.“.
    const mixed = otherContainers((rows ?? []).map((r) => catalog.articles.get(r.article)));
    const unit = mixed ? "" : " (Fl.)";
    const out: unknown[][] = [[drinks ? "Getränk" : "Wein", "Lager", "Zählungen", `Differenz${unit}`, "Differenz (€)", `Bekannte Verluste${unit}`, "Bekannte Verluste (€)", ...(mixed ? ["Gebinde"] : [])]];
    for (const r of rows ?? []) {
      const a = catalog.articles.get(r.article);
      out.push([name(r.article), catalog.lagers.get(r.lager)?.name ?? r.lager, r.counts, fl(r, r.qty), euroCell(r.value), fl(r, r.known), euroCell(r.knownValue), ...(mixed ? [a ? containerWords(a).many : ""] : [])]);
    }
    if (!prices) for (const [i, row] of out.entries()) out[i] = row.filter((_, col) => col !== 4 && col !== 6);
    download(`Schwund ${de(from)} bis ${de(to)}.csv`, toCsv(out), "text/csv;charset=utf-8");
  }

  return (
    <section className="card">
      <h2>Schwund</h2>
      <p className="muted">Je {drinks ? "Getränk" : "Wein"} die Differenzen aller Zählungen im Zeitraum; daneben, was als Bruch, Korkfehler oder Verderb gebucht wurde.</p>
      <div className="row dates">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="von" data-testid="shrink-from" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="bis" data-testid="shrink-to" />
        <button className="primary" disabled={busy} onClick={load} data-testid="shrink-load">
          {busy ? "Rechnet …" : "Anzeigen"}
        </button>
      </div>
      {error && <p className="error-box">{error}</p>}
      {rows && (
        <>
          <p className="muted">{source}</p>
          {rows.length === 0 ? (
            <p>Im Zeitraum keine Differenzen und keine bekannten Verluste.</p>
          ) : (
            <>
              <ul className="list">
                {rows.map((r) => (
                  <li key={`${r.lager}|${r.article}`} className={`row between ${r.qty < 0 ? "negative" : ""}`}>
                    <span>
                      {name(r.article)}
                      <br />
                      <span className="muted">
                        {catalog.lagers.get(r.lager)?.name ?? r.lager} · {r.counts} {r.counts === 1 ? "Zählung" : "Zählungen"}
                        {r.known ? ` · bekannt ${fmt(catalog.articles.get(r.article), r.known)}${prices ? ` (${euro(r.knownValue)})` : ""}` : ""}
                      </span>
                    </span>
                    <span className="side">
                      <span>
                        {fmtDiff(catalog.articles.get(r.article), r.qty)}
                      </span>
                      {prices && <span>{euro(r.value)}</span>}
                    </span>
                  </li>
                ))}
              </ul>
              {/* Shrinkage and more than booked apart, never netted (as in the inventory report). */}
              <p className="row between negative" data-testid="shrink-short">
                <strong>Schwund</strong>
                <strong className="side">
                  <Lines parts={total((r) => r.short)} />
                  {prices && <span>{euro(sum((r) => r.shortValue))}</span>}
                </strong>
              </p>
              {rows.some((r) => r.over > 0) && (
                <p className="row between" data-testid="shrink-over">
                  <span>mehr als gebucht</span>
                  <span className="side">
                    <Lines parts={total((r) => r.over, true)} />
                    {prices && <span>{euro(sum((r) => r.overValue))}</span>}
                  </span>
                </p>
              )}
              <p className="row between">
                <span>Bekannte Verluste</span>
                <span className="side">
                  <Lines parts={total((r) => r.known)} />
                  {prices && <span>{euro(sum((r) => r.knownValue))}</span>}
                </span>
              </p>
              <button onClick={csv}>Als Excel-Tabelle (CSV)</button>
            </>
          )}
        </>
      )}
    </section>
  );
}

/** Stock value now by category (the wine's colour) and storage (R2), on the device, also offline. */
function ByCategory({ catalog }: { catalog: Catalog }) {
  const { store } = useStore();
  const ledger = useLedger() as State;
  const wide = useWide();
  const lagers = [...catalog.lagers.values()].filter((l) => store.config?.lagers.includes(l.id));
  const key = WINE_PROFILE.categoryField ?? "category";
  const cells = new Map<string, { qty: Containers; value: number }>();
  const cats = new Set<string>();
  for (const line of stockLines(ledger)) {
    if (!store.config?.lagers.includes(line.lager)) continue;
    const a = catalog.articles.get(line.article);
    const cat = String(a?.fields[key] ?? a?.category ?? "ohne Angabe");
    cats.add(cat);
    // Per profile too (docs/17-getraenke.md): „@spirituosen“ sums the spirits.
    const profile = `@${(a && drinkProfile(a)) ?? "wein"}`;
    for (const k of [`${cat}|${line.lager}`, `${cat}|*`, `*|${line.lager}`, "*|*", `${profile}|${line.lager}`, `${profile}|*`]) {
      const c = cells.get(k) ?? { qty: new Containers(), value: 0 };
      // Bottles, kegs and cans apart; food in g has a value here, but no containers to add up.
      c.qty.add(a, line.qty);
      c.value += line.value;
      cells.set(k, c);
    }
  }
  const order = [...ALL_KINDS, "ohne Angabe"] as string[];
  const kindRows = [...cats].sort((a, b) => (order.indexOf(a) + 1 || 999) - (order.indexOf(b) + 1 || 999) || a.localeCompare(b));
  // With more than one profile, each profile's sum heads its kinds.
  const profiles = PROFILES.filter((p) => cells.has(`@${p.id}|*`));
  const rows = profiles.length > 1 ? profiles.flatMap((p) => [`@${p.id}`, ...kindRows.filter((k) => (kindProfile(k)?.id ?? "wein") === p.id)]) : kindRows;
  const rowLabel = (row: string) => (row.startsWith("@") ? PROFILE_TITLES[row.slice(1)] : row);
  const prices = store.prices;
  const cell = (k: string) => {
    const c = cells.get(k);
    if (c && !prices) return <Lines parts={c.qty.parts()} />;
    return c ? (
      <>
        <span>{euro(c.value)}</span>
        {c.qty.total !== 0 &&
          c.qty.parts().map((p, i, all) => (
            <span key={p} className="muted">
              {i < all.length - 1 ? `${p} +` : p}
            </span>
          ))}
      </>
    ) : (
      <span className="muted">–</span>
    );
  };
  return (
    <section className="card">
      <h2>
        {prices ? "Lagerwert" : "Bestand"} {profiles.length > 1 ? "nach Profil und Art" : "nach Farbe"}
      </h2>
      <p className="muted">Jetzt{prices ? ", zum Einkaufspreis," : ""} aus den Daten dieses Geräts.</p>
      {!wide ? (
        // On a phone one block per storage: the colours one per line, the value in the right column.
        [...lagers.map((l) => [l.id, l.name] as const), ["*", lagers.length > 1 ? "Alle Lager" : ""] as const]
          .filter(([id, label]) => label && cells.has(`*|${id}`))
          .map(([id, label]) => (
            <div key={id} className="by-lager">
              <h3 className="row between">
                <span>{label}</span>
                <span className="side">{cell(`*|${id}`)}</span>
              </h3>
              <ul className="list">
                {rows
                  .filter((cat) => cells.has(`${cat}|${id}`))
                  .map((cat) => (
                    <li key={cat} className={`row between ${cat.startsWith("@") ? "profile-sum" : ""}`}>
                      <span>{cat.startsWith("@") ? <strong>{rowLabel(cat)}</strong> : rowLabel(cat)}</span>
                      <span className="side">{cell(`${cat}|${id}`)}</span>
                    </li>
                  ))}
              </ul>
            </div>
          ))
      ) : (
      <table className="grid">
        <thead>
          <tr>
            <th>{profiles.length > 1 ? "Profil, Art" : "Farbe"}</th>
            {lagers.map((l) => (
              <th key={l.id} className="num">
                {l.name}
              </th>
            ))}
            <th className="num">Summe</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((cat) => (
            <tr key={cat} className={cat.startsWith("@") ? "profile-sum" : ""}>
              <td>{cat.startsWith("@") ? <strong>{rowLabel(cat)}</strong> : rowLabel(cat)}</td>
              {lagers.map((l) => (
                <td key={l.id} className="num stack-cell">
                  {cell(`${cat}|${l.id}`)}
                </td>
              ))}
              <td className="num stack-cell">{cell(`${cat}|*`)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>Summe</th>
            {lagers.map((l) => (
              <th key={l.id} className="num stack-cell">
                {cell(`*|${l.id}`)}
              </th>
            ))}
            <th className="num stack-cell">{cell("*|*")}</th>
          </tr>
        </tfoot>
      </table>
      )}
    </section>
  );
}
